import { Suspense, useState } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Combobox from '@givebutter/primitives/combobox';
import { Dot } from '@givebutter/primitives/dot';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { DATE_MASK_FORMAT } from '@givebutter/util';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { transformAddressToRequest } from 'api/account';
import { donate } from 'api/transactions';
import { SelectAppeal } from 'assets/js/pages/account-settings/account-settings/appeals/SelectAppeal';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import { SelectedContact } from 'assets/js/pages/contacts/SelectedContact';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import {
    TransactionDedicationInput,
    TransactionFundInput,
    TransactionTeamInput,
    TransactionTeamMemberInput,
    TransactionsFormValues,
} from 'assets/js/pages/transactions/TransactionForm';
import { SoftCreditFormFields } from 'assets/js/pages/transactions/soft-credits/SoftCreditFormFields';
import { transformSoftCreditToUpstream } from 'assets/js/pages/transactions/soft-credits/transforms';
import { AddressForm, useResetAddressFields } from 'components/address-form/AddressForm';
import { transformServerAddressIntoFormAddress } from 'components/address-form/useAccountAddressForm';
import * as CustomFieldResponseForm from 'components/custom-fields/CustomFieldResponseForm';
import { toFormValues, toRequestValues } from 'components/custom-fields/helpers';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as EditableInput from 'components/editable-input';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentPossibleContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { checkAddressErrors, useFormServerErrors } from 'customHooks/useFormServerErrors';
import { useInfiniteCampaignsByActiveStatus } from 'customHooks/useInfiniteCampaignsByActiveStatus';
import { useSelectedCampaignCustomFields } from 'customHooks/useSelectedCampaignCustomFields';
import { maskedDateToServerDateTime } from 'helpers/dates';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import {
    Campaign,
    Contact,
    ContactEmail,
    ContactPhone,
    TransactionDonation,
    TransactionMethod,
} from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const EphemeralId = ContactForm.NewContactId;

const TransactionsDetailsFormDrawer = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { goBack } = useDialogRoute();
    const { accountId } = useCurrentAccount();
    const { data: currentContact } = useCurrentPossibleContact();
    const [saveAndExit, setSaveAndExit] = useState(false);
    const [searchCampaigns, setSearchCampaigns] = useState('');
    const [isAddingTransactionOfflineFile, setIsAddingTransactionOfflineFile] = useState(false);
    const debouncedSearchCampaigns = useDebounce(searchCampaigns);
    const {
        transactionSoftCredits = false,
        toggleCheckReceived = false,
        appeals = false,
    } = useFlags();
    const [selectedCampaignData, setSelectedCampaignData] = useState<Campaign | null>(null);

    const { data: campaignsData, ref: campaignsRef } = useInfiniteCampaignsByActiveStatus({
        query: debouncedSearchCampaigns,
        sortBy: 'active',
    });
    const campaigns = campaignsData?.pages ?? [];
    const formAddressFields = currentContact?.primary_address
        ? transformServerAddressIntoFormAddress(currentContact.primary_address)
        : {
              line1: '',
              line2: '',
              city: '',
              zip: '',
              stateUs: '',
              stateIntl: '',
              country: 'USA',
              isPrimary: true,
          };

    const methods = useGivebutterForm<TransactionsFormValues>({
        defaultValues: {
            ...formAddressFields,
            amount: '',
            checkNumber: '',
            offlinePaymentReceived: false,
            paymentMethod: 'cash' as TransactionMethod,
            donationDate: DateTime.now().toFormat('MM/dd/yyyy'),
            depositDate: '',
            hideName: false,
            hideAmount: false,
            sendReceipt: false,
            firstName: '',
            lastName: '',
            contactType: ContactType.Individual,
            companyName: '',
            message: '',
            internalNote: '',
            emails: [{ value: '', is_primary: true, type: 'personal' }],
            phones: [{ value: '', is_primary: true, type: 'home' }],
            selectedFund: [],
            selectedContact: currentContact
                ? [
                      {
                          id: currentContact.id.toString(),
                          label: getSelectedContactLabel(currentContact),
                          value: currentContact,
                      },
                  ]
                : [],
            selectedCampaign: [],
            selectedTeamMember: [],
            selectedTeam: [],
            customFields: {},
            dedicationName: '',
            dedicationType: 'in_honor_of',
            dedicationRecipientEmail: '',
            dedicationRecipientName: '',
            dedicationShouldNotifyRecipient: false,
            shouldAddDedication: false,
            softCredit: {
                selectedContact: [],
                firstName: '',
                lastName: '',
                companyName: '',
                contactType: ContactType.Individual,
                emails: [{ value: '', is_primary: true, type: 'personal' }],
                phones: [{ value: '', is_primary: true, type: 'home' }],
                amount: '',
                note: '',
            },
            appeal_id: [],
        },
    });

    const {
        setError,
        path,
        control,
        resetFormButton,
        handleSubmit,
        watch,
        setValue,
        resetField,
        reset,
    } = methods;

    const { setAddressErrors } = useFormServerErrors(methods);
    const resetAddressFields = useResetAddressFields(methods);

    const [
        paymentMethod,
        rawSelectedCampaign,
        rawSelectedContact,
        rawSelectedMember,
        rawSelectedTeam,
        rawSelectedFund,
        customFieldsRaw,
        shouldAddDedication,
        contactType,
        softCredit,
    ] = watch([
        'paymentMethod',
        'selectedCampaign',
        'selectedContact',
        'selectedTeamMember',
        'selectedTeam',
        'selectedFund',
        'customFields',
        'shouldAddDedication',
        'contactType',
        'softCredit',
    ]);

    const selectedCampaign = rawSelectedCampaign?.[0];
    const selectedContact = rawSelectedContact?.[0];
    const selectedMember = rawSelectedMember?.[0];
    const selectedTeam = rawSelectedTeam?.[0];
    const selectedFund = rawSelectedFund?.[0];
    const isEphemeralContact = selectedContact?.id === EphemeralId;
    const isEventCampaign = selectedCampaignData?.type === 'event';

    const { mutate: submit, isPending: isSubmitting } = useMutation({
        mutationFn: (formValues: TransactionsFormValues) => {
            const campaignId = selectedCampaign ? parseInt(selectedCampaign) : null;
            const hasSelectedContact = selectedContact && parseInt(selectedContact.id) > 0;
            const contactId = hasSelectedContact ? parseInt(selectedContact.id) : null;
            const contactType = hasSelectedContact
                ? selectedContact.value?.type
                : formValues.contactType;
            const memberId = selectedMember ? parseInt(selectedMember) : null;
            const teamId = selectedTeam ? parseInt(selectedTeam) : null;
            const fundId = selectedFund ? parseInt(selectedFund) : null;
            const hasDedication =
                formValues.shouldAddDedication && formValues.dedicationName.length > 0;
            const shouldNotify = hasDedication && formValues.dedicationShouldNotifyRecipient;
            const hasDedicationRecipientNameAndEmail =
                shouldNotify &&
                formValues.dedicationRecipientEmail.length > 0 &&
                formValues.dedicationRecipientName.length > 0;
            const hasSoftCredit =
                !!formValues.softCredit.amount && formValues.softCredit.selectedContact.length > 0;

            const state = formValues.country === 'USA' ? formValues.stateUs : formValues.stateIntl;
            let requestAddressFields = transformAddressToRequest({ ...formValues, state });

            if (selectedContact?.value?.primary_address) {
                const contactAddress = selectedContact.value.primary_address;
                requestAddressFields = transformAddressToRequest({
                    country: contactAddress.country ?? '',
                    line1: contactAddress.address_1 ?? '',
                    line2: contactAddress.address_2 ?? '',
                    city: contactAddress.city,
                    zip: contactAddress.zipcode,
                    state: contactAddress.state,
                });
            }

            const isOfflinePaymentReceivedVisible =
                formValues.paymentMethod === 'check' || formValues.paymentMethod === 'cash';

            return donate({
                ...requestAddressFields,
                hide_name: formValues.hideName,
                hide_amount: formValues.hideAmount,
                payment_method: formValues.paymentMethod,
                first_name: formValues.firstName,
                last_name: formValues.lastName,
                email: formValues.emails[0]?.value,
                phone: formValues.phones[0]?.value,
                check_number: formValues.checkNumber ?? '',
                ...(toggleCheckReceived && {
                    offline_payment_received: isOfflinePaymentReceivedVisible
                        ? formValues.offlinePaymentReceived
                        : undefined,
                }),
                deposited_at:
                    formValues.depositDate.length > 0
                        ? maskedDateToServerDateTime(formValues.depositDate)
                        : '',
                transacted_at:
                    formValues.donationDate.length > 0
                        ? maskedDateToServerDateTime(formValues.donationDate)
                        : '',
                amount: formValues.amount,
                send_confirmation_email: formValues.sendReceipt,
                message: formValues.message,
                internal_note: formValues.internalNote,
                fund: fundId,
                campaign_id: campaignId,
                contact_id: contactId,
                contact_type: contactType,
                company_name: formValues.companyName || selectedContact?.value?.company_name,
                member: memberId,
                team: teamId,
                custom_fields: customFields
                    ? toRequestValues(customFieldsRaw, customFields, { useId: true })
                    : null,
                dedication_name: hasDedication ? formValues.dedicationName : undefined,
                dedication_type: hasDedication ? formValues.dedicationType : undefined,
                dedication_recipient_name: hasDedicationRecipientNameAndEmail
                    ? formValues.dedicationRecipientName
                    : undefined,
                dedication_recipient_email: hasDedicationRecipientNameAndEmail
                    ? formValues.dedicationRecipientEmail
                    : undefined,
                soft_credit: hasSoftCredit
                    ? transformSoftCreditToUpstream(formValues.softCredit)
                    : undefined,
                appeal_id: formValues.appeal_id?.[0] ? formValues.appeal_id[0] : null,
            });
        },

        onSuccess: () => {
            toast.success('Transaction created successfully!');
            queryClient.invalidateQueries({
                queryKey: transactionQueryKeys.transactions(accountId),
                type: 'all',
            });

            if (currentContact) {
                queryClient.invalidateQueries({
                    queryKey: accountQueryKeys.contacts(accountId),
                });
            }

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });

            if (saveAndExit) {
                goBack();
            } else {
                reset();
            }
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data?.message ?? `An unknown error occurred.`);
            resetFormButton();

            const errors = error.response?.data.errors;
            if (errors) {
                const hasAddressErrors = checkAddressErrors(errors);
                if (hasAddressErrors) {
                    setAddressErrors(errors);
                }

                Object.keys(errors).forEach(key => {
                    const normalizedKey = transformServerKeyIntoFormKey(
                        key as Parameters<typeof transformServerKeyIntoFormKey>[0],
                    );

                    if (normalizedKey?.includes('customFields')) {
                        const customFieldErrors = errors[key];

                        Object.keys(customFieldErrors).forEach(key => {
                            setError(`customFields.${key}`, {
                                message: customFieldErrors[key][0],
                            });
                        });

                        return;
                    }

                    const message = errors[key][0];

                    if (normalizedKey?.includes('phones')) {
                        setError('phones.0.value', {
                            message,
                        });

                        return;
                    }

                    if (normalizedKey?.includes('emails')) {
                        setError('emails.0.value', {
                            message,
                        });

                        return;
                    }

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<TransactionsFormValues>, {
                            message,
                        });
                    }
                });
            }
        },
    });

    const { customFields, selectCampaign } = useSelectedCampaignCustomFields({
        onSuccess: customFields => {
            setValue('customFields', toFormValues(customFields));
        },
    });

    const resetContact = () => {
        resetField('companyName');
        resetField('firstName');
        resetField('lastName');
        resetField('emails');
        resetField('phones');
        resetAddressFields();
    };

    return (
        <Form.Provider {...methods}>
            <form
                className={'contents'}
                onSubmit={handleSubmit(submit as SubmitHandler<TransactionsFormValues>)}
            >
                <Drawer.Header>
                    <Drawer.Title>New transaction</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className="flex flex-col gap-6">
                    <p className={'font-medium'}>Transaction details</p>
                    <Form.Container>
                        <Form.Label name={path('selectedCampaign')}>
                            Campaign
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.Combobox
                            name="selectedCampaign"
                            control={control}
                            allowMultiple={false}
                            rules={{
                                required: 'Select a campaign',
                            }}
                            onChangeValue={value => {
                                const id = parseInt(value[0]);
                                if (id) {
                                    selectCampaign(id);
                                    setSelectedCampaignData(
                                        campaigns.find(c => c.id === id) ?? null,
                                    );
                                }
                                setSearchCampaigns('');
                                setValue('selectedTeamMember', []);
                                setValue('selectedTeam', []);
                                setValue('selectedFund', []);
                                setValue('customFields', {});
                            }}
                        >
                            <Combobox.Trigger aria-label="Select a campaign">
                                <Combobox.Select placeholder="Select a campaign" />
                            </Combobox.Trigger>
                            <Combobox.Popover>
                                <Combobox.Search
                                    value={searchCampaigns}
                                    onChangeValue={setSearchCampaigns}
                                />
                                <Combobox.Items>
                                    {campaigns.map((item: Campaign) => (
                                        <Combobox.Item value={item.id.toString()} key={item.id}>
                                            <Dot
                                                className={
                                                    item.active ? 'bg-green-500' : 'bg-red-500'
                                                }
                                            />
                                            <Combobox.ItemText>
                                                <span className="text-sm font-medium">
                                                    {item.title}
                                                </span>
                                            </Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                    ))}
                                    {campaigns.length === 0 && (
                                        <Combobox.NoneFoundText>
                                            No campaigns found
                                        </Combobox.NoneFoundText>
                                    )}
                                    <div ref={campaignsRef} />
                                </Combobox.Items>
                            </Combobox.Popover>
                        </Form.Combobox>
                        <Form.Error name={path('selectedCampaign')} />
                        {isEventCampaign && (
                            <p>
                                <a
                                    className="text-sm text-blue-600 hover:underline"
                                    href={selectedCampaignData?.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Purchase tickets
                                </a>{' '}
                                for this transaction on the campaign page.
                            </p>
                        )}
                    </Form.Container>
                    <EditableInput.Root>
                        <Form.Container>
                            <Form.Label name={path('selectedContact')}>
                                Contact
                                <Form.LabelAsterisk />
                            </Form.Label>
                            {(!selectedContact || isEphemeralContact) && (
                                <ContactForm.SelectContact nameRequired={true} />
                            )}
                            {!!selectedContact && !isEphemeralContact && (
                                <div className={'flex items-start gap-4'}>
                                    <SelectedContact contact={selectedContact} />
                                    {!currentContact ? (
                                        <Button
                                            variant="none"
                                            color="neutral"
                                            onClick={() => {
                                                resetField('selectedContact');
                                                resetContact();
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </Button>
                                    ) : null}
                                </div>
                            )}
                            <Form.Error name={path('selectedContact')} />
                        </Form.Container>
                    </EditableInput.Root>
                    {isEphemeralContact && (
                        <div className={'flex flex-col gap-4'}>
                            <CompanyForm.TypeToggle
                                name="contactType"
                                onChange={value => {
                                    setValue('emails', getDefaultEmailsForContactType(value));
                                    setValue('phones', getDefaultPhonesForContactType(value));
                                }}
                            />
                            {contactType === ContactType.Company && <CompanyForm.Name />}
                            {contactType === ContactType.Individual && (
                                <div className="flex flex-row gap-4">
                                    <ContactForm.FirstName hideAffix />
                                    <ContactForm.LastName hideAffix />
                                </div>
                            )}
                            <ContactForm.Emails />
                            <ContactForm.Phones />
                            <AddressForm />
                        </div>
                    )}
                    {!isEphemeralContact &&
                        !!selectedContact &&
                        !selectedContact.value?.primary_address && <AddressForm />}
                    <Form.Container>
                        <Form.Label name={path('amount')}>
                            Amount
                            <Form.LabelAsterisk />
                        </Form.Label>
                        <Form.TextInputRoot
                            control={control}
                            name={'amount'}
                            rules={{
                                required: 'Amount is required',
                            }}
                            inputRootProps={{
                                mask: 'decimal',
                            }}
                        >
                            <InputCurrencyIcon />
                            <Form.TextInputForm placeholder={'0.00'} />
                        </Form.TextInputRoot>
                        {['in-kind', 'stock', 'property', 'other'].includes(paymentMethod) && (
                            <p className={'text-sm text-gray-600'}>
                                This signifies the estimated value of the gift. Non-monetary
                                contributions may have a $0.00 value.
                            </p>
                        )}
                        <Form.Error name={path('amount')} />
                    </Form.Container>
                    <div className="flex flex-row gap-4">
                        <Form.Container>
                            <Form.Label name={path('donationDate')}>
                                Donation date
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.TextInputRoot
                                control={control}
                                name={'donationDate'}
                                rules={{
                                    required: 'Donation date is required',
                                    validate: value => {
                                        const parsedDate = DateTime.fromFormat(
                                            value,
                                            DATE_MASK_FORMAT,
                                        );
                                        return (
                                            parsedDate.isValid ||
                                            'Date must be in MM/DD/YYYY format'
                                        );
                                    },
                                }}
                                inputRootProps={{
                                    mask: 'date',
                                }}
                            >
                                <Form.TextInputForm placeholder="MM/DD/YYYY" />
                            </Form.TextInputRoot>
                            <Form.Error name={path('donationDate')} />
                        </Form.Container>
                        <Form.Container>
                            <Form.Label name={path('paymentMethod')}>
                                Payment method
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.SingleSelect control={control} name="paymentMethod">
                                <SingleSelect.Trigger arial-label="Payment method" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="cash">
                                        <SingleSelect.ItemText>Cash</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="check">
                                        <SingleSelect.ItemText>Check</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="in-kind">
                                        <SingleSelect.ItemText>In-kind</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="stock">
                                        <SingleSelect.ItemText>Stock</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="property">
                                        <SingleSelect.ItemText>Property</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="other">
                                        <SingleSelect.ItemText>Other</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </Form.Container>
                    </div>
                    {paymentMethod === 'cash' && toggleCheckReceived && (
                        <Form.Container>
                            <div className="flex flex-row justify-items-start gap-2">
                                <Form.UseFormCheckboxInput
                                    control={control}
                                    name="offlinePaymentReceived"
                                    id="offlinePaymentReceived"
                                />
                                <div className="flex flex-col gap-0.5">
                                    <Form.Label
                                        name={path('offlinePaymentReceived')}
                                        className="leading-none"
                                    >
                                        Cash payment received
                                        <p className="text-sm font-normal">
                                            Marks pending cash payment as a succeeded payment.
                                        </p>
                                    </Form.Label>
                                </div>
                            </div>
                            <Form.Error name={path('offlinePaymentReceived')} />
                        </Form.Container>
                    )}
                    {paymentMethod === 'check' && (
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-row gap-4">
                                <Form.Container>
                                    <Form.Label name={path('depositDate')}>Deposit date</Form.Label>
                                    <Form.TextInputRoot
                                        control={control}
                                        name={'depositDate'}
                                        inputRootProps={{
                                            mask: 'date',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder="MM/DD/YYYY" />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('depositDate')} />
                                </Form.Container>
                                <Form.Container>
                                    <Form.Label name={path('checkNumber')}>Check number</Form.Label>
                                    <Form.TextInputRoot control={control} name={'checkNumber'}>
                                        <Form.TextInputForm placeholder={'Check number'} />
                                    </Form.TextInputRoot>
                                    <Form.Error name={path('checkNumber')} />
                                </Form.Container>
                            </div>
                            {toggleCheckReceived && (
                                <Form.Container>
                                    <div className="flex flex-row justify-items-start gap-2">
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name="offlinePaymentReceived"
                                            id="offlinePaymentReceived"
                                        />
                                        <div className="flex flex-col gap-0.5">
                                            <Form.Label
                                                name={path('offlinePaymentReceived')}
                                                className="leading-none"
                                            >
                                                Check received
                                                <p className="text-sm font-normal">
                                                    Marks pending check as a succeeded payment.
                                                </p>
                                            </Form.Label>
                                        </div>
                                    </div>
                                    <Form.Error name={path('offlinePaymentReceived')} />
                                </Form.Container>
                            )}
                        </div>
                    )}
                    {selectedCampaignData?.has_members && (
                        <Suspense fallback={null}>
                            <Form.Container>
                                <Form.Label name={path('selectedTeamMember')}>Member</Form.Label>
                                <TransactionTeamMemberInput
                                    key={selectedCampaign}
                                    campaignId={parseInt(selectedCampaign)}
                                />
                            </Form.Container>
                        </Suspense>
                    )}
                    {selectedCampaignData?.has_teams && (
                        <Suspense fallback={null}>
                            <Form.Container>
                                <Form.Label name={path('selectedTeam')}>Team</Form.Label>
                                <TransactionTeamInput
                                    key={selectedCampaign}
                                    campaignId={parseInt(selectedCampaign)}
                                />
                            </Form.Container>
                        </Suspense>
                    )}
                    {selectedCampaignData?.has_funds && (
                        <Suspense fallback={null}>
                            <Form.Container>
                                <Form.Label name={path('selectedFund')}>Fund</Form.Label>
                                <TransactionFundInput />
                            </Form.Container>
                        </Suspense>
                    )}
                    <Form.Container>
                        <Form.Label name={path('message')}>Message</Form.Label>
                        <Form.Textarea
                            control={control}
                            name={'message'}
                            placeholder={'Type message'}
                            rows={8}
                        />
                        <p className={'text-sm text-gray-600'}>
                            Message will appear in the supporter feed if the campaign is active.
                        </p>
                    </Form.Container>
                    <Form.Container>
                        <Form.Label name={path('internalNote')}>Internal note</Form.Label>
                        <Form.Textarea
                            control={control}
                            name={path('internalNote')}
                            placeholder={'Type a note'}
                            rows={8}
                        />
                        <p className={'text-sm text-gray-600'}>
                            Internal note for admin use. Will not be displayed to donors.
                        </p>
                    </Form.Container>

                    {transactionSoftCredits && (
                        <Form.Container>
                            <p className="text-sm font-medium">Soft credit</p>
                            <SoftCreditFormFields required={!!softCredit.amount} />
                        </Form.Container>
                    )}

                    {appeals && (
                        <Form.Container>
                            <Form.Label name={path('appeal_id')}>Appeal</Form.Label>
                            <SelectAppeal control={control} name="appeal_id" />
                            <Form.Error name={path('appeal_id')} />
                        </Form.Container>
                    )}

                    <div className={'flex flex-col gap-2'}>
                        <Form.Container className={'flex-row gap-2'}>
                            <Form.UseFormCheckboxInput
                                control={control}
                                name={'shouldAddDedication'}
                            />
                            <div className={'flex flex-col gap-1'}>
                                <Form.Label
                                    className={'leading-none'}
                                    name={path('shouldAddDedication')}
                                >
                                    Add dedication
                                </Form.Label>
                                <p className={'text-sm text-gray-600'}>
                                    Dedicate this gift in honor or memory of someone.
                                </p>
                            </div>
                        </Form.Container>
                        {shouldAddDedication && (
                            <Box className={'overflow-visible p-4'}>
                                <TransactionDedicationInput />
                            </Box>
                        )}
                    </div>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('sendReceipt')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('sendReceipt')} className={'leading-none'}>
                                Send a receipt
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                After saving, a receipt will be sent to the contact’s email address.
                            </p>
                        </div>
                    </Form.Container>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('hideName')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('hideName')} className={'leading-none'}>
                                Do not display name publicly
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                When enabled, the contact’s name will be hidden on the public-facing
                                campaign page.
                            </p>
                        </div>
                    </Form.Container>
                    <Form.Container className={'flex-row gap-2'}>
                        <Form.UseFormCheckboxInput control={control} name={path('hideAmount')} />
                        <div className={'flex flex-col gap-1'}>
                            <Form.Label name={path('hideAmount')} className={'leading-none'}>
                                Do not display amount publicly
                            </Form.Label>
                            <p className={'text-sm text-gray-600'}>
                                The amount donated will be hidden on the public-facing campaign
                                page.
                            </p>
                        </div>
                    </Form.Container>
                    {!!selectedCampaign &&
                        !!customFields &&
                        customFields.length > 0 &&
                        Object.keys(customFieldsRaw).length > 0 && (
                            <>
                                <div>
                                    <Separator className={'-mx-8 !w-auto'} />
                                </div>
                                <div className={'flex flex-col'}>
                                    <p className={'font-medium'}>Custom fields</p>
                                    <p className={'text-sm text-gray-600'}>
                                        The campaign you selected has custom fields.
                                    </p>
                                </div>
                                {customFields.map(field => (
                                    <CustomFieldResponseForm.Root
                                        key={field.id}
                                        field={field}
                                        onAddFile={setIsAddingTransactionOfflineFile}
                                    >
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col">
                                                <CustomFieldResponseForm.Title />
                                                <CustomFieldResponseForm.Description />
                                            </div>

                                            <div className="flex flex-col">
                                                <CustomFieldResponseForm.Input />
                                                <CustomFieldResponseForm.FormError />
                                            </div>
                                        </div>
                                    </CustomFieldResponseForm.Root>
                                ))}
                            </>
                        )}
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton
                                variant={'outline'}
                                color={'neutral'}
                                noAnimation={true}
                                onClick={() => setSaveAndExit(false)}
                                isSubmitting={isAddingTransactionOfflineFile || isSubmitting}
                            >
                                Save and add another
                            </Form.SubmitButton>
                            <Form.SubmitButton
                                isSubmitting={isAddingTransactionOfflineFile || isSubmitting}
                                onClick={() => setSaveAndExit(true)}
                            >
                                Save
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const getDefaultEmailsForContactType = (contactType: string): ContactEmail[] => {
    return [
        {
            value: '',
            is_primary: true,
            type: contactType === ContactType.Company ? 'work' : 'personal',
        } as ContactEmail,
    ];
};

const getDefaultPhonesForContactType = (contactType: string): ContactPhone[] => {
    return [
        {
            value: '',
            is_primary: true,
            type: contactType === ContactType.Company ? 'work' : 'home',
        },
    ];
};

const getSelectedContactLabel = (contact: Contact) => {
    return `${contact.first_name} ${contact.last_name}${
        !!contact.primary_email ? ` - ${contact.primary_email}` : ''
    }`;
};

const transformServerKeyIntoFormKey = (
    serverKey: keyof TransactionDonation | 'campaign',
): keyof TransactionsFormValues | null => {
    switch (serverKey) {
        case 'first_name':
            return 'firstName';
        case 'last_name':
            return 'lastName';
        case 'payment_method':
            return 'paymentMethod';
        case 'deposited_at':
            return 'depositDate';
        case 'check_number':
            return 'checkNumber';
        case 'hide_amount':
            return 'hideAmount';
        case 'hide_name':
            return 'hideName';
        case 'send_confirmation_email':
            return 'sendReceipt';
        case 'transacted_at':
            return 'donationDate';
        case 'member':
            return 'selectedTeamMember';
        case 'campaign':
        case 'campaign_id':
            return 'selectedCampaign';
        case 'contact_id':
            return 'selectedContact';
        case 'fund':
            return 'selectedFund';
        case 'custom_fields':
            return 'customFields';
        case 'address_1':
        case 'address_2':
        case 'state':
        case 'zipcode':
        case 'city':
        case 'country':
            return null;
        case 'email':
            return 'emails';
        case 'phone':
            return 'phones';
        case 'amount':
        case 'message':
            return serverKey;
        case 'offline_payment_received':
            return 'offlinePaymentReceived';
        default:
            return null;
    }
};

export { TransactionsDetailsFormDrawer };
export type { TransactionsFormValues };
